import React, { useEffect, useRef } from "react";
import styles from "./collection-list.module.scss";
import SkeletonLoader from "@/components/layout/skeleton-loader/skeleton-loader";
import type { GeneralTokenCollectionType } from "@/features/bridge/types/open-sea-api.types";
import { useNftTokenManager } from "@/features/bridge/stores/nft-token-manager";
import { useMultiTokenManager } from "@/features/bridge/stores/multi-token-manager";
import { useTokenManagerStore } from "@/features/bridge/stores/token-type.store";
import MultiCard from "@/features/bridge/components/token-selector/selector-interface/multi-card";
import NftCard from "@/features/bridge/components/token-selector/selector-interface/nft-card";
import { TokenType } from "@/features/bridge/types/token-selector.types";
import Toolbar from "@/features/bridge/components/token-selector/selector-interface/toolbar";
import TokensNotFound from "@/features/bridge/components/token-selector/selector-interface/tokens-not-found";
import { useTokenFilterStore } from "@/features/bridge/stores/token-filter.store";
import { useTokenEligibility } from "@/features/bridge/hooks/useTokenEligibility.hook";
import { useCollectionEligibility } from "@/features/bridge/hooks/useCollectionEligibility.hook";
import type { ModifiedNftToken } from "@/features/bridge/types/open-sea-api.types";

type Props = {
  collections: GeneralTokenCollectionType;
  isLoading: boolean;
  selectAllTokens: () => void;
  getSelectionCount: (collection: string) => number;
  refetchTokens: () => void;
};

// Component to check eligibility and conditionally render token
const FilteredTokenCard = ({ token, currentlySelectedTokenType }: { token: ModifiedNftToken; currentlySelectedTokenType: TokenType }) => {
  const { showOnlyBridgeableTokens } = useTokenFilterStore();
  const eligibility = useTokenEligibility(token.address);

  // If filtering is enabled and token is not eligible, don't render
  if (showOnlyBridgeableTokens && !eligibility.isEligible) {
    return null;
  }

  const Card = currentlySelectedTokenType === TokenType.MULTI ? MultiCard : NftCard;

  return (
    <Card
      key={token.collection + token.identifier}
      token={token}
      collection={token.collection}
    />
  );
};

const CardList = ({
  collections,
  isLoading,
  getSelectionCount,
  selectAllTokens,
  refetchTokens,
}: Props) => {
  const { activeCollection: activeNftCollection } = useNftTokenManager();
  const { activeCollection: activeMultiCollection } = useMultiTokenManager();

  const ref = useRef<HTMLDivElement>(null);

  const { currentlySelectedTokenType } = useTokenManagerStore();

  // Use the correct card corresponding on the currentlyActiveToken
  const Card =
    currentlySelectedTokenType === TokenType.MULTI ? MultiCard : NftCard;
  const activeCollection =
    currentlySelectedTokenType === TokenType.MULTI
      ? activeMultiCollection
      : activeNftCollection;

  // Check if the active collection is eligible for bridging
  const activeCollectionData = collections[activeCollection || ""];
  const collectionEligibility = useCollectionEligibility(activeCollectionData);

  useEffect(() => {
    if (ref.current) ref.current.scrollTop = 0;
  }, [activeCollection]);

  return (
    <>
      <Toolbar
        selectAllTokens={collectionEligibility.hasEligibleTokens ? selectAllTokens : undefined}
        selectionCount={getSelectionCount(activeCollection || "")}
        isEnabled={!isLoading && !!collections[activeCollection || 0]}
        refetchTokens={refetchTokens}
      />
      <div className={styles.collectionList} ref={ref}>
        {!isLoading &&
          currentlySelectedTokenType &&
          collections[activeCollection || 0]?.tokenData?.map((token) => {
            return (
              <FilteredTokenCard
                key={token.collection + token.identifier}
                token={token}
                currentlySelectedTokenType={currentlySelectedTokenType}
              />
            );
          })}
        {isLoading &&
          Array.from({ length: 6 }).map((v, i) => <SkeletonLoader key={i} />)}
        {!isLoading && !collections[activeCollection || 0] && (
          <TokensNotFound size="large" />
        )}
      </div>
    </>
  );
};

export default CardList;
